import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster } from 'sonner';
import { ArrowLeft, CircleNotch, Sparkle, Flame, Snowflake } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const signalColor = (tag) => {
  if (tag.includes('STRONG HOT')) return 'bg-orange-100 text-orange-900 ring-2 ring-orange-500';
  if (tag.includes('STRONG COLD')) return 'bg-blue-100 text-blue-900 ring-2 ring-blue-500';
  if (tag.includes('HOT CONFLUENCE')) return 'bg-orange-50 text-orange-800 ring-1 ring-orange-300';
  if (tag.includes('COLD REBOUND')) return 'bg-blue-50 text-blue-800 ring-1 ring-blue-300';
  if (tag.includes('BAYESIAN-ONLY')) return 'bg-indigo-50 text-indigo-800 ring-1 ring-indigo-300';
  if (tag.includes('low Bayes')) return 'bg-gray-100 text-gray-600';
  return 'bg-gray-50 text-gray-400';
};

const scoreColor = (s) => {
  if (s >= 6) return 'bg-emerald-100 text-emerald-900 ring-2 ring-emerald-400 font-black';
  if (s >= 4) return 'bg-lime-100 text-lime-800 font-bold';
  if (s >= 2.5) return 'bg-amber-50 text-amber-700';
  return 'bg-gray-50 text-gray-500';
};

export default function Window64Confluence() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tolerance, setTolerance] = useState('medium');

  const fetchData = useCallback(async (tol) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/confluence`, { params: { tolerance: tol } });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(tolerance); }, [tolerance, fetchData]);

  useEffect(() => {
    const h = () => fetchData(tolerance);
    window.addEventListener('euromillions:draws-updated', h);
    return () => window.removeEventListener('euromillions:draws-updated', h);
  }, [tolerance, fetchData]);

  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      <header className="bg-white border-b">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded flex items-center justify-center"><Sparkle size={24} weight="fill" className="text-yellow-300" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Window 64 — Confluence Finder</h1>
              <p className="text-xs text-gray-500">W22 Bayesian × W63 Reversion — numbers where both windows agree</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-semibold">W22 Tolerance:</label>
            {['tight', 'medium', 'loose'].map((t) => (
              <button key={t} onClick={() => setTolerance(t)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${tolerance === t ? 'bg-purple-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`tol-${t}`}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W64" {...NARRATIVES.W64} className="mb-5" />
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-purple-600" /></div>
        ) : data?.error ? (
          <div className="bg-rose-50 border border-rose-200 rounded p-6 text-center text-rose-700 font-bold">{data.error}</div>
        ) : data ? (
          <>
            {/* Summary context header */}
            <div className="bg-gradient-to-br from-white to-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-4" data-testid="block-header">
              <p className="text-xs text-gray-700">
                <Sparkle size={14} weight="fill" className="inline text-purple-600 mr-1" />
                Best-confluence pick per ball (W22 Bayesian + W63 Reversion agreement). Confidence: <b>{data.confidence?.toUpperCase()}</b> · <b>{data.n_precedents}</b> precedents · Tolerance <b>{data.tolerance.toUpperCase()}</b>
              </p>
            </div>

            {/* Blocks 1–5 — Top Confluence Pick per Ball (one block per ball) */}
            <div className="space-y-4 mb-6">
              {ballKeys.map((bk, idx) => {
                const n = idx + 1;
                const pick = data.top_picks?.[bk];
                const ballLabel = data.ball_labels?.[bk] || `B${n}`;
                return (
                  <section key={bk} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`w64-block-${n}`}>
                    <header className="bg-slate-800 text-white px-4 py-2.5">
                      <h3 className="font-bold text-sm tracking-tight">
                        <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {n}</span>
                        Top Confluence Pick · {ballLabel} (Ball {n})
                      </h3>
                      <p className="text-[11px] text-white/70 mt-0.5">Strongest W22 × W63 agreement for this ball position.</p>
                    </header>
                    <div className="p-4">
                      {!pick ? (
                        <div className="bg-gray-50 rounded p-4 text-sm text-gray-500 italic" data-testid={`no-pick-${bk}`}>
                          No confluence at or above the minimum score threshold for this ball. Consider increasing W22 tolerance.
                        </div>
                      ) : (
                        <div className={`rounded-lg p-5 border-2 ${scoreColor(pick.confluence_score)}`} data-testid={`top-${bk}`}>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                            {/* Big number + score */}
                            <div className="flex items-center gap-4">
                              <div className="text-6xl font-mono font-black leading-none">{pick.number}</div>
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">Confluence score</div>
                                <div className="text-3xl font-mono font-black">{pick.confluence_score}</div>
                                <div className={`text-[10px] font-bold mt-1 px-1.5 py-0.5 rounded inline-block ${signalColor(pick.signal_tag)}`}>{pick.signal_tag}</div>
                              </div>
                            </div>

                            {/* W22 stats */}
                            <div className="bg-white/60 rounded p-3 border border-white/80">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 mb-1">W22 · Bayesian</div>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <div className="text-[9px] text-gray-500">BF</div>
                                  <div className="text-lg font-mono font-black text-indigo-700">{pick.w22?.bayes_factor}×</div>
                                </div>
                                <div>
                                  <div className="text-[9px] text-gray-500">Post%</div>
                                  <div className="text-lg font-mono font-bold text-indigo-500">{pick.w22?.posterior_pct}%</div>
                                </div>
                                <div>
                                  <div className="text-[9px] text-gray-500">n</div>
                                  <div className="text-lg font-mono font-bold text-gray-600">{pick.w22?.sample_count}</div>
                                </div>
                              </div>
                            </div>

                            {/* W63 stats */}
                            <div className="bg-white/60 rounded p-3 border border-white/80">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 mb-1">W63 · Reversion</div>
                              <div className="grid grid-cols-4 gap-1 text-center">
                                <div>
                                  <div className="text-[9px] text-gray-500">State</div>
                                  <div className="text-[10px] font-bold">
                                    {pick.w63?.state === 'HOT' ? (
                                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700"><Flame size={10} weight="fill" />HOT</span>
                                    ) : pick.w63?.state === 'COLD' ? (
                                      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-100 text-blue-700"><Snowflake size={10} weight="fill" />COLD</span>
                                    ) : <span className="text-gray-400">NEUT</span>}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[9px] text-gray-500">Lift</div>
                                  <div className="text-sm font-mono font-bold">{pick.w63?.lift}×</div>
                                </div>
                                <div>
                                  <div className="text-[9px] text-gray-500">Gap</div>
                                  <div className="text-sm font-mono">{pick.w63?.current_gap}/{pick.w63?.avg_gap}</div>
                                </div>
                                <div>
                                  <div className="text-[9px] text-gray-500">Trig</div>
                                  <div className="text-sm font-mono font-bold">
                                    {pick.w63?.n_triggers_fired >= 2 ? <span className="text-amber-700">🔥{pick.w63?.n_triggers_fired}/5</span>
                                      : pick.w63?.n_triggers_fired === 1 ? <span className="text-gray-600">⚡1/5</span>
                                      : <span className="text-gray-300">—</span>}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Description */}
                            <div className="bg-white/60 rounded p-3 border border-white/80">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Why this pick?</div>
                              <p className="text-xs text-gray-700 leading-snug">{pick.signal_description}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>

            {/* Block 2 — Per-Ball Confluence Tables */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-per-ball-tables">
              <h2 className="font-bold text-gray-900 mb-1">Block 6 — Full Confluence Table Per Ball (Top 10)</h2>
              <p className="text-xs text-gray-500 mb-4">For each ball, shows the top 10 W22 picks with their W63 reversion context. Rows ordered by confluence score.</p>
              <div className="grid grid-cols-1 gap-6">
                {ballKeys.map((bk) => {
                  const rows = data.per_ball?.[bk] || [];
                  return (
                    <div key={bk} className="border rounded overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-700 to-pink-700 text-white px-4 py-2 font-bold flex items-center justify-between">
                        <span>{data.ball_labels[bk]} — Top Confluence Picks</span>
                        <span className="text-xs opacity-80">{rows.length} candidates</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-2 text-left">Rank</th>
                              <th className="px-2 py-2 text-left">#</th>
                              <th className="px-2 py-2 text-center" colSpan="3">W22 Bayesian</th>
                              <th className="px-2 py-2 text-center" colSpan="4">W63 Reversion</th>
                              <th className="px-2 py-2 text-center">Score</th>
                              <th className="px-2 py-2 text-left">Signal</th>
                            </tr>
                            <tr className="bg-gray-50 text-[10px] uppercase text-gray-500">
                              <th colSpan="2"></th>
                              <th className="px-2 py-1 text-center">BF</th>
                              <th className="px-2 py-1 text-center">Post%</th>
                              <th className="px-2 py-1 text-center">n</th>
                              <th className="px-2 py-1 text-center">State</th>
                              <th className="px-2 py-1 text-center">Lift</th>
                              <th className="px-2 py-1 text-center">Gap/Avg</th>
                              <th className="px-2 py-1 text-center">Triggers</th>
                              <th colSpan="2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => (
                              <tr key={r.number} className={`border-b hover:bg-gray-50 ${i === 0 ? 'bg-amber-50/40 font-bold' : ''}`} data-testid={`row-${bk}-${r.number}`}>
                                <td className="px-2 py-1.5 font-mono text-gray-500">{i + 1}</td>
                                <td className="px-2 py-1.5 font-mono text-base font-black">{r.number}</td>
                                <td className="px-2 py-1.5 text-center font-mono font-bold text-indigo-700">{r.w22.bayes_factor}×</td>
                                <td className="px-2 py-1.5 text-center font-mono text-indigo-500">{r.w22.posterior_pct}%</td>
                                <td className="px-2 py-1.5 text-center font-mono text-gray-400">{r.w22.sample_count}</td>
                                <td className="px-2 py-1.5 text-center">
                                  {r.w63.state === 'HOT' ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold"><Flame size={10} weight="fill" />HOT</span>
                                  ) : r.w63.state === 'COLD' ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold"><Snowflake size={10} weight="fill" />COLD</span>
                                  ) : <span className="text-gray-400 text-[10px]">NEUTRAL</span>}
                                </td>
                                <td className="px-2 py-1.5 text-center font-mono">{r.w63.lift}×</td>
                                <td className="px-2 py-1.5 text-center font-mono text-gray-500">{r.w63.current_gap}/{r.w63.avg_gap}</td>
                                <td className="px-2 py-1.5 text-center">
                                  {r.w63.n_triggers_fired >= 2 ? <span className="text-amber-700 font-bold">🔥 {r.w63.n_triggers_fired}/5</span> :
                                   r.w63.n_triggers_fired === 1 ? <span className="text-gray-600">⚡ 1/5</span> :
                                   <span className="text-gray-300">—</span>}
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded font-mono font-black ${scoreColor(r.confluence_score)}`}>{r.confluence_score}</span>
                                </td>
                                <td className="px-2 py-1.5">
                                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${signalColor(r.signal_tag)}`}>{r.signal_tag}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Block 3 — Signal Distribution */}
            <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-signal-counts">
              <h2 className="font-bold text-gray-900 mb-1">Block 7 — Signal Distribution (Top 5 per ball across all 5 positions)</h2>
              <p className="text-xs text-gray-500 mb-4">How many positions show each type of confluence signal. Heavy concentration in one type tells you the current draw profile's regime.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.signal_counts || {}).sort(([,a],[,b]) => b-a).map(([tag, count]) => (
                  <div key={tag} className={`rounded p-3 ${signalColor(tag)}`}>
                    <div className="text-[10px] font-bold">{tag}</div>
                    <div className="text-3xl font-mono font-black">{count}</div>
                    <div className="text-[10px] opacity-70">occurrences in top-5 across 5 balls</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Block 4 — Methodology */}
            <section className="bg-slate-800 text-white rounded-lg p-5" data-testid="block-methodology">
              <h2 className="font-bold mb-3">Block 8 — How the Confluence Score Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="font-bold text-amber-300 mb-1">Bayesian component (W22)</p>
                  <p className="text-white/80">max(0, BF − 0.5) × 2. BF≥1.5 adds strong weight. BF&lt;0.5 contributes 0.</p>
                </div>
                <div>
                  <p className="font-bold text-amber-300 mb-1">State bonus (W63)</p>
                  <p className="text-white/80">HOT: +1.5. COLD: +1.0 (+1.5 if overdue≥2, +0.8 if overdue≥1.5).</p>
                </div>
                <div>
                  <p className="font-bold text-amber-300 mb-1">Trigger bonus (W63)</p>
                  <p className="text-white/80">3+ fired: +2.0. 2 fired: +1.2. 1 fired: +0.4.</p>
                </div>
              </div>
              <p className="text-[11px] text-white/60 mt-4 italic">
                Score ≥ 6 = both windows strongly agree. Score 4–6 = moderate confluence. Score 2–4 = weak. &lt;2 = signals don't align.
              </p>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
